import {
  CalendarHeart,
  Clapperboard,
  MessagesSquare,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import { Seo } from "@/components/brand/Seo";
import { OperatingModel } from "@/components/brand/OperatingModel";
import {
  BorderBox,
  DarkSection,
  Eyebrow,
  GhostButton,
  OrangeButton,
  Section,
} from "@/components/brand/Bits";
import { CONTACT, ENGAGEMENT_MODELS, FUNNEL, SCOPING, SERVICES } from "@/data/siteContent";

const ICONS = { Users, Clapperboard, MessagesSquare, TrendingUp, Search, CalendarHeart };

export default function Services() {
  return (
    <>
      <Seo
        path="/services"
        title="Services — KOL Marketing, Content, Ads, SEO & Events | AnyTribe"
        description="The whole creator funnel under one team: KOL marketing, content creation, threads and seeder posting, performance ads, SEO & SEM, and community & events across Malaysia."
      />

      <DarkSection testId="services-hero" className="pt-20 sm:pt-24">
        <div className="max-w-3xl">
          <Eyebrow dark>{FUNNEL.eyebrow}</Eyebrow>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
            {FUNNEL.title}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-white/75 md:text-lg">{FUNNEL.body}</p>
          <p className="mt-7 font-heading text-[15px] font-bold leading-relaxed text-[#FFB25C] sm:text-base">
            {FUNNEL.logic}
          </p>
        </div>
      </DarkSection>

      <Section testId="services-list">
        <div className="grid gap-5 md:grid-cols-2">
          {SERVICES.map((service) => {
            const Icon = ICONS[service.icon] || Users;
            return (
              <BorderBox
                key={service.slug}
                className="transition-colors duration-200 hover:border-[#F2811D]"
                testId={`service-card-${service.slug}`}
              >
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#FDF0E2]">
                    <Icon className="h-5 w-5 text-[#D96C0C]" />
                  </span>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#F2811D]">
                      {service.stage}
                    </p>
                    <h2 className="mt-2 font-heading text-xl font-extrabold">{service.name}</h2>
                    <p className="mt-2.5 text-[15px] leading-relaxed body-text">
                      {service.description}
                    </p>
                  </div>
                </div>
              </BorderBox>
            );
          })}
        </div>
      </Section>

      <OperatingModel />

      <Section tone="cream" testId="engagement-models">
        <div className="max-w-3xl">
          <Eyebrow>{ENGAGEMENT_MODELS.eyebrow}</Eyebrow>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
            {ENGAGEMENT_MODELS.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed body-text md:text-lg">
            {ENGAGEMENT_MODELS.body}
          </p>
        </div>

        <div className="mt-11 grid gap-4 md:grid-cols-2">
          {ENGAGEMENT_MODELS.models.map((model) => (
            <BorderBox key={model.id} testId={`model-${model.id}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#F2811D]">
                {model.label}
              </p>
              <h3 className="mt-3 font-heading text-lg font-extrabold leading-snug">{model.title}</h3>
              <p className="mt-3 text-sm font-bold text-[#111111]">{model.bestFor}</p>
              <p className="mt-2 text-sm leading-relaxed body-text">{model.description}</p>
            </BorderBox>
          ))}
        </div>

        <p className="mt-7 max-w-3xl text-[15px] leading-relaxed body-text">
          {ENGAGEMENT_MODELS.addOn.split("@joinsaltandsoul")[0]}
          <a
            href={CONTACT.saltAndSoul.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-[#F2811D] hover:underline"
            data-testid="salt-and-soul-link"
          >
            {CONTACT.saltAndSoul.handle}
          </a>
          .
        </p>
      </Section>

      <DarkSection testId="services-scoping">
        <div className="max-w-3xl">
          <Eyebrow dark>{SCOPING.eyebrow}</Eyebrow>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
            {SCOPING.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">{SCOPING.body}</p>
        </div>
        <div className="mt-11 grid gap-4 md:grid-cols-3">
          {SCOPING.steps.map((step) => (
            <BorderBox key={step.n} dark testId={`scoping-${step.n.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#FFB25C]">
                {step.n}
              </p>
              <h3 className="mt-3 font-heading text-base font-extrabold text-white">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{step.body}</p>
            </BorderBox>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <OrangeButton to="/contact" testId="services-cta-contact">
            Work With Us
          </OrangeButton>
          <GhostButton to="/join" dark testId="services-cta-join">
            Creators: join the pool
          </GhostButton>
        </div>
      </DarkSection>
    </>
  );
}
